const locks = new Set();
let scrollTopPosition: number;

/**
 * Prevents body scrolling. Keeps track of which elements requested a lock so multiple levels of locking are possible
 * without premature unlocking.
 * Uses `scrollTop` and `position: fixed` to disable scrolling while keeping an existing scrollbar visible,
 * which avoids layout jumps.
 */
export function lockBodyScrolling(lockingEl: HTMLElement) {
    const hasVScroll = hasVerticalScroll();
    if (!hasVScroll) {
        return;
    }

    if (locks.size === 0) {
        scrollTopPosition = document.documentElement.scrollTop;
        document.body.style.top = `-${scrollTopPosition}px`;
        document.body.classList.add("uic-u-scroll-lock");
    }

    locks.add(lockingEl);
}

/**
 * Unlocks body scrolling. Scrolling will only be unlocked once all elements that requested a lock call this method.
 * Resets the scroll position to the one before locking.
 */
export function unlockBodyScrolling(lockingEl: HTMLElement) {
    if (locks.delete(lockingEl) && locks.size === 0) {
        document.body.classList.remove("uic-u-scroll-lock");
        window.scrollTo(0, scrollTopPosition);
    }
}

/**
 * Checks the height of the element or page to detect a vertical scrollbar
 * Uses different methods which allow for higher cross browser compatibility:
 * https://stackoverflow.com/questions/2146874/detect-if-a-page-has-a-vertical-scrollbar
 * @param node the dom element, if not given uses the body and window
 * @returns true if the page or element has a scrollbar
 */
export function hasVerticalScroll(node: HTMLElement = undefined) {
    // eslint-disable-next-line @stencil-community/strict-boolean-conditions
    if (!node) {
        // eslint-disable-next-line @stencil-community/strict-boolean-conditions
        if (window.innerHeight) {
            return document.body.offsetHeight > window.innerHeight;
        }
        return (
            document.documentElement.scrollHeight > document.documentElement.offsetHeight ||
            document.body.scrollHeight > document.body.offsetHeight
        );
    }
    return node.scrollHeight > node.offsetHeight;
}
